import { centerOpticalCount } from '@theme/optical-count';
import { normalizeWheelDeltas } from '@theme/monolyt-v22-gallery';
import { hasConstrainedMediaConnection } from './monolyt-media-policy.js?v=2600083df17ccd19bc7a6adc6f5ae417cc3913aaa3119ca87d4e893b97c56623';

const ROOT_SELECTOR = '[data-pageify-root]';
const BACKGROUND_HOST_SELECTOR = '[data-pageify-background-host]';
const BACKGROUND_LAYER_SELECTOR = '[data-pageify-background-layer]';
const instances = new WeakMap();
const liveInstances = new Set();
let generatedId = 0;

class MonolytPageify {
  constructor(root) {
    this.root = root;
    this.index = 0;
    this.sequence = 0;
    this.turnLocked = false;
    this.wheelDeltaTotal = 0;
    this.wheelIgnoreUntil = 0;
    this.wheelLastInputAt = 0;
    this.wheelLastTurnAt = 0;
    this.wheelGestureBlockedUntilQuiet = false;
    this.wheelGestureUsedNativeScroll = false;
    this.readingWheel = null;
    this.touch = null;
    this.timers = new Set();
    this.backgroundLayerPortals = new Map();
    this.mediaPlayAttempts = new WeakMap();
    this.mediaLoopProgress = new WeakMap();
    this.mediaBufferChecks = new WeakMap();
    this.mediaListenerControllers = new WeakMap();
    this.abortController = new AbortController();
    this.reducedMotion = window.matchMedia('(prefers-reduced-motion: reduce)');
    this.connection = navigator.connection;

    if (!this.buildRegistry()) return;
    this.index = this.resolveInitialIndex();

    this.navigation = this.findNavigation();
    if (!this.navigation) return;

    this.previousButton = this.navigation.querySelector('[data-pageify-prev]');
    this.nextButton = this.navigation.querySelector('[data-pageify-next]');
    this.currentCounter = this.navigation.querySelector('[data-pageify-current]');
    this.totalCounter = this.navigation.querySelector('[data-pageify-total]');
    this.statusCounter = this.navigation.querySelector('[data-pageify-status]');
    if (!this.previousButton || !this.nextButton || !this.currentCounter || !this.totalCounter) return;
    if (!this.portalBackgroundLayers()) return;

    this.connected = true;
    this.bindEvents();
    this.commitState(this.index, 0);
    this.root.classList.add('is-enhanced');
    this.navigation.removeAttribute('data-pageify-unready');
    this.navigation.removeAttribute('aria-hidden');
    this.previousButton.removeAttribute('tabindex');
    this.nextButton.removeAttribute('tabindex');
    document.body.classList.add('pageify-is-enhanced');
    document.documentElement.classList.remove('monolyt-pageify-pending');
    this.setupMobileProductSheet();
  }

  setupMobileProductSheet() {
    this.mobileProductSheetCleanup?.();
    const viewport = this.root.querySelector(
      '.monolyt-product-pageify-panel--v2-pdp-candidate '
      + '.monolyt-product-pageify-viewport--mobile-split:not(.monolyt-product-pageify-viewport--has-additional-blocks)'
    );
    const media = viewport?.querySelector('.product-information__media');
    const details = viewport?.querySelector('.product-details');
    const content = details?.querySelector(':scope > .group-block > .group-block-content[data-pageify-scrollable]');
    const scroller = media?.parentElement;
    if (!media || !details || !content || !scroller?.classList.contains('product-information__grid')) return;

    const mobile = window.matchMedia('(max-width: 749px)');
    const savedAttributes = ['data-pageify-scrollable', 'data-pageify-scrollbar']
      .map((name) => [name, scroller.getAttribute(name)]);
    let observer;
    let enabled = false;
    let height = 0;
    let previousContentScroll = 0;
    const measure = () => {
      const previousHeight = height;
      const previousScroll = scroller.scrollTop;
      height = viewport.clientHeight;
      // Layout changes only. Sheet, text and gallery no longer need a scroll listener:
      // the native scrollport and CSS sticky own every frame, including iOS momentum.
      scroller.style.setProperty('--monolyt-mobile-sheet-height', `${viewport.clientHeight}px`);
      if (enabled && previousHeight > 0 && previousHeight !== height) {
        // Keep the same reading position when browser chrome changes the viewport.
        const previousOffset = previousHeight / 2;
        const offset = height / 2;
        scroller.scrollTop = previousScroll >= previousOffset
          ? offset + previousScroll - previousOffset
          : previousScroll * offset / previousOffset;
      }
    };
    const disable = () => {
      if (!enabled) return;
      observer?.disconnect();
      scroller.scrollTop = 0;
      scroller.removeAttribute('data-monolyt-mobile-sheet');
      savedAttributes.forEach(([name, value]) => {
        if (value === null) scroller.removeAttribute(name);
        else scroller.setAttribute(name, value);
      });
      scroller.style.removeProperty('--monolyt-mobile-sheet-height');
      content.scrollTop = previousContentScroll;
      height = 0;
      enabled = false;
    };
    const sync = () => {
      if (!mobile.matches) return disable();
      if (enabled) return measure();
      previousContentScroll = content.scrollTop;
      content.scrollTop = 0;
      measure();
      scroller.setAttribute('data-monolyt-mobile-sheet', '');
      scroller.setAttribute('data-pageify-scrollable', '');
      scroller.setAttribute('data-pageify-scrollbar', 'hidden');
      enabled = true;
      observer = new ResizeObserver(measure);
      observer.observe(viewport);
    };
    mobile.addEventListener('change', sync);
    sync();
    this.mobileProductSheetCleanup = () => {
      disable();
      mobile.removeEventListener('change', sync);
    };
  }

  ownedElements(selector) {
    return [...this.root.querySelectorAll(selector)].filter((element) => element.closest(ROOT_SELECTOR) === this.root);
  }

  findBackgroundHost() {
    return [...this.root.children].find((element) => element.matches(BACKGROUND_HOST_SELECTOR)) || null;
  }

  pruneBackgroundLayerPortals() {
    this.backgroundLayerPortals.forEach((portal, layer) => {
      if (portal.anchor.isConnected && this.root.contains(portal.anchor)) return;
      layer.remove();
      portal.anchor.remove();
      this.backgroundLayerPortals.delete(layer);
    });
  }

  sortBackgroundHostLayers() {
    const portals = [...this.backgroundLayerPortals.values()].filter((portal) => portal.anchor.isConnected);
    portals.sort((first, second) => {
      const position = first.anchor.compareDocumentPosition(second.anchor);
      if (position & Node.DOCUMENT_POSITION_FOLLOWING) return -1;
      if (position & Node.DOCUMENT_POSITION_PRECEDING) return 1;
      return 0;
    });
    portals.forEach(({ layer }) => this.backgroundHost.append(layer));
  }

  portalBackgroundLayers() {
    this.backgroundHost = this.findBackgroundHost();
    if (!this.backgroundHost) return false;

    this.pruneBackgroundLayerPortals();
    const layers = this.ownedElements(BACKGROUND_LAYER_SELECTOR)
      .filter((layer) => !this.backgroundLayerPortals.has(layer));
    layers.forEach((layer) => {
      // The anchor preserves provider ownership and exact source order while the visual layer shares one root host.
      const anchor = document.createComment(' monolyt-pageify-background-layer ');
      layer.before(anchor);
      this.backgroundLayerPortals.set(layer, { anchor, layer });
      this.backgroundHost.append(layer);
    });

    if (!this.backgroundLayerPortals.size) return false;
    this.sortBackgroundHostLayers();
    this.root.setAttribute('data-pageify-backgrounds-hosted', '');
    return true;
  }

  restoreBackgroundLayers(scope = null) {
    this.backgroundLayerPortals.forEach((portal, layer) => {
      const { anchor } = portal;
      if (scope && scope !== anchor.parentNode && !scope.contains?.(anchor)) return;

      if (anchor.parentNode) {
        anchor.replaceWith(layer);
      } else {
        layer.remove();
      }
      this.backgroundLayerPortals.delete(layer);
    });

    if (!this.backgroundLayerPortals.size) {
      this.root.removeAttribute('data-pageify-backgrounds-hosted');
    }
  }

  buildRegistry() {
    const panels = this.ownedElements('[data-pageify-panel]');
    const backgrounds = this.ownedElements('[data-pageify-background]');
    if (!panels.length || panels.length !== backgrounds.length) return false;

    const panelKeys = panels.map((panel, index) => panel.dataset.pageifyKey || String(index));
    const backgroundKeys = backgrounds.map((background, index) => background.dataset.pageifyKey || String(index));
    if (new Set(panelKeys).size !== panelKeys.length || new Set(backgroundKeys).size !== backgroundKeys.length) return false;

    const backgroundsByKey = new Map(backgrounds.map((background, index) => [backgroundKeys[index], background]));
    if (!panelKeys.every((key) => backgroundsByKey.has(key))) return false;

    this.panels = panels;
    this.panelKeys = panelKeys;
    this.backgrounds = panelKeys.map((key) => backgroundsByKey.get(key));
    this.stageSynchronizedBackgrounds = Boolean(this.findBackgroundHost());
    return true;
  }

  resolveInitialIndex() {
    let hashId = window.location.hash.slice(1);
    try {
      hashId = decodeURIComponent(hashId);
    } catch {
      // Keep the browser-provided hash when it is not valid percent-encoding.
    }
    const hashTarget = hashId ? document.getElementById(hashId) : null;
    const autofocusTarget = this.root.querySelector('[autofocus]');
    const focusedTarget = document.activeElement instanceof Element ? document.activeElement : null;

    for (const target of [hashTarget, autofocusTarget, focusedTarget]) {
      if (!(target instanceof Element)) continue;
      const index = this.panels.findIndex((panel) => panel.contains(target));
      if (index >= 0) return index;
    }

    return 0;
  }

  findNavigation() {
    if (!this.root.id) this.root.id = `MonolytPageify-${++generatedId}`;
    const navigation = document.querySelector(`[data-pageify-navigation][aria-controls="${CSS.escape(this.root.id)}"]`)
      || document.querySelector('[data-pageify-navigation]:not([aria-controls])');
    this.navigationHadAriaControls = navigation?.hasAttribute('aria-controls') || false;
    navigation?.setAttribute('aria-controls', this.root.id);
    return navigation;
  }

  bindEvents() {
    const signal = this.abortController.signal;
    this.previousButton.addEventListener('click', () => this.turn(-1, 'button'), { signal });
    this.nextButton.addEventListener('click', () => this.turn(1, 'button'), { signal });
    this.root.addEventListener('keydown', (event) => this.onKeydown(event), { signal });
    this.navigation.addEventListener('keydown', (event) => this.onKeydown(event), { signal });
    this.root.addEventListener('wheel', (event) => this.onWheel(event), { capture: true, passive: false, signal });
    this.root.addEventListener('pointerdown', () => { this.readingWheel = null; }, { capture: true, signal });
    this.root.addEventListener('pointerout', (event) => {
      if (this.readingWheel && (!(event.relatedTarget instanceof Node) || !this.readingWheel.owner.contains(event.relatedTarget))) {
        this.readingWheel = null;
      }
    }, { signal });
    this.root.addEventListener('touchstart', (event) => this.onTouchStart(event), { passive: true, signal });
    this.root.addEventListener('touchmove', (event) => this.onTouchMove(event), { passive: false, signal });
    this.root.addEventListener('touchend', (event) => this.onTouchEnd(event), { passive: true, signal });
    this.root.addEventListener('touchcancel', () => this.resetTouch(), { passive: true, signal });
    document.addEventListener('visibilitychange', () => {
      if (document.hidden) {
        this.pauseMedia();
      } else {
        this.syncMedia();
      }
    }, { signal });
    this.onMotionPreferenceChange = () => this.syncMedia();
    this.reducedMotion.addEventListener?.('change', this.onMotionPreferenceChange);
    this.onConnectionChange = () => this.syncMedia();
    this.connection?.addEventListener?.('change', this.onConnectionChange);

    if (this.stageSynchronizedBackgrounds && typeof ResizeObserver === 'function') {
      this.backgroundResizeObserver = new ResizeObserver(() => this.syncOffsets());
      this.backgroundResizeObserver.observe(this.root);
      this.backgroundResizeObserver.observe(this.backgroundHost);
    } else if (this.stageSynchronizedBackgrounds) {
      window.addEventListener('resize', () => this.syncOffsets(), { signal });
    }
  }

  schedule(callback, delay) {
    const timer = window.setTimeout(() => {
      this.timers.delete(timer);
      callback();
    }, delay);
    this.timers.add(timer);
    return timer;
  }

  eventDetail(fromIndex, toIndex, direction, source) {
    return {
      fromIndex,
      toIndex,
      direction,
      source,
      fromPanel: this.panels[fromIndex],
      toPanel: this.panels[toIndex],
    };
  }

  goTo(requestedIndex, source = 'api') {
    const toIndex = Math.max(0, Math.min(requestedIndex, this.panels.length - 1));
    const fromIndex = this.index;
    if (toIndex === fromIndex) return false;

    const direction = toIndex > fromIndex ? 1 : -1;
    const detail = this.eventDetail(fromIndex, toIndex, direction, source);
    const beforeChange = new CustomEvent('pageify:beforechange', {
      bubbles: true,
      cancelable: true,
      detail,
    });
    if (!this.root.dispatchEvent(beforeChange)) return false;

    const outgoingPanel = this.panels[fromIndex];
    const focusedElement = document.activeElement;
    if (outgoingPanel) {
      outgoingPanel.classList.remove('is-active');
      outgoingPanel.classList.add('is-leaving');
      this.schedule(() => outgoingPanel.classList.remove('is-leaving'), this.reducedMotion.matches ? 0 : 1000);
    }

    if (focusedElement instanceof Element && outgoingPanel?.contains(focusedElement)) {
      this.root.focus({ preventScroll: true });
    }
    this.commitState(toIndex, direction);

    this.root.dispatchEvent(new CustomEvent('pageify:change', { bubbles: true, detail }));
    const changeSequence = ++this.sequence;
    this.schedule(() => {
      if (changeSequence !== this.sequence || !this.connected) return;
      this.root.dispatchEvent(new CustomEvent('pageify:afterchange', { bubbles: true, detail }));
    }, this.reducedMotion.matches ? 0 : 1000);
    return true;
  }

  commitState(index, direction) {
    this.index = index;
    this.root.dataset.pageifyActive = this.panelKeys[index];
    this.root.dataset.pageifyDirection = direction < 0 ? 'previous' : 'next';
    this.root.dataset.turnDirection = direction < 0 ? 'previous' : 'next';

    this.panels.forEach((panel, panelIndex) => {
      const active = panelIndex === index;
      panel.style.zIndex = active ? '2' : '1';
      panel.classList.toggle('is-active', active);
      if (active) panel.classList.remove('is-leaving');
      panel.toggleAttribute('inert', !active);
      panel.setAttribute('aria-hidden', active ? 'false' : 'true');
    });

    this.syncOffsets(index);
    this.backgrounds.forEach((background, backgroundIndex) => {
      background.classList.toggle('is-active', backgroundIndex === index);
      background.setAttribute('aria-hidden', 'true');
    });

    this.currentCounter.textContent = String(index + 1);
    this.totalCounter.textContent = String(this.panels.length);
    centerOpticalCount(this.currentCounter);
    centerOpticalCount(this.totalCounter);
    if (this.statusCounter) {
      const statusTemplate = this.statusCounter.dataset.pageifyStatusTemplate || 'Chapter __CURRENT__ of __TOTAL__';
      this.statusCounter.textContent = statusTemplate
        .replace('__CURRENT__', String(index + 1))
        .replace('__TOTAL__', String(this.panels.length));
    }
    this.previousButton.disabled = index === 0;
    this.nextButton.disabled = index === this.panels.length - 1;
    this.navigation.dataset.pageifyAtEnd = index === this.panels.length - 1 ? 'true' : 'false';
    this.syncMedia();
  }

  trackPositions() {
    const rootRect = this.root.getBoundingClientRect();
    const step = rootRect.height;
    let position = 0;
    let headerGap = 0;

    const positions = this.panels.map((panel, panelIndex) => {
      if (panelIndex > 0) position += step;
      if (panel.dataset.pageifyGapBefore === 'header') {
        const gap = Math.max(0, rootRect.top);
        position += gap;
        headerGap = Math.max(headerGap, gap);
      }
      return position;
    });

    this.root.style.setProperty('--pageify-header-gap', `${headerGap}px`);
    return positions;
  }

  syncOffsets(index = this.index) {
    const positions = this.trackPositions();
    const activePosition = positions[index] ?? 0;

    this.panels.forEach((panel, panelIndex) => {
      panel.style.setProperty('--pageify-offset', `${positions[panelIndex] - activePosition}px`);
    });
    this.backgrounds.forEach((background, backgroundIndex) => {
      background.style.setProperty('--pageify-offset', `${positions[backgroundIndex] - activePosition}px`);
    });
  }

  turn(direction, source) {
    if (this.turnLocked) return false;
    const targetIndex = Math.max(0, Math.min(this.index + direction, this.panels.length - 1));
    if (targetIndex === this.index) return false;
    if (!this.goTo(targetIndex, source)) return false;

    this.turnLocked = true;
    this.schedule(() => {
      this.turnLocked = false;
    }, this.reducedMotion.matches ? 0 : 500);
    return true;
  }

  isBlockedTarget(target) {
    if (!(target instanceof Element) || document.querySelector('dialog[open]')) return true;
    return Boolean(target.closest('form, label, input, textarea, select, option, button, [contenteditable="true"], [data-pageify-gesture-ignore]'));
  }

  onKeydown(event) {
    const navigationControl = event.target instanceof Element
      && event.target.closest('[data-pageify-prev], [data-pageify-next]');
    const pageifyKeyboardTarget = event.target === this.root || navigationControl;
    if (
      event.defaultPrevented
      || event.repeat
      || event.altKey
      || event.ctrlKey
      || event.metaKey
      || event.shiftKey
      || !pageifyKeyboardTarget
      || document.querySelector('dialog[open]')
    ) return;

    const previousKeys = ['ArrowUp', 'PageUp'];
    const nextKeys = ['ArrowDown', 'PageDown'];
    let targetIndex = null;
    if (previousKeys.includes(event.key)) targetIndex = this.index - 1;
    if (nextKeys.includes(event.key)) targetIndex = this.index + 1;
    if (event.key === 'Home') targetIndex = 0;
    if (event.key === 'End') targetIndex = this.panels.length - 1;
    if (targetIndex === null) return;

    event.preventDefault();
    if (targetIndex === this.index || this.turnLocked) return;
    if (Math.abs(targetIndex - this.index) === 1) {
      this.turn(targetIndex > this.index ? 1 : -1, 'keyboard');
    } else {
      this.goTo(targetIndex, 'keyboard');
    }
  }

  normalizeWheelDelta(event) {
    return normalizeWheelDeltas(event, this.root).y;
  }

  readingOwner(target) {
    const details = target.closest('.product-details');
    if (!details || details.closest(ROOT_SELECTOR) !== this.root) return null;
    const mobileSheet = details.closest('[data-monolyt-mobile-sheet]');
    if (mobileSheet && ['auto', 'scroll', 'overlay'].includes(getComputedStyle(mobileSheet).overflowY)) return mobileSheet;
    let owner = target.closest('[data-pageify-scrollable]');
    while (owner && (owner === details || details.contains(owner))) {
      if (['auto', 'scroll', 'overlay'].includes(getComputedStyle(owner).overflowY)) return owner;
      owner = owner.parentElement?.closest('[data-pageify-scrollable]');
    }
    return null;
  }

  onReadingWheel(event, owner, deltaY, now) {
    const previous = this.readingWheel;
    const direction = Math.sign(deltaY);
    const extent = `${owner.scrollHeight}:${owner.clientHeight}`;
    const same = previous?.owner === owner && previous.direction === direction && previous.extent === extent;
    const fresh = !previous || now - previous.lastAt > 420;
    if (!same || fresh) {
      this.readingWheel = { owner, direction, extent, lastAt: now, total: 0, events: 0,
        armed: false, nativeUsed: false, eligible: Boolean(same && fresh && (previous.armed || previous.nativeUsed || previous.eligible)) };
    }
    const state = this.readingWheel;
    state.lastAt = now;
    if (this.findScrollable(event.target, deltaY)) {
      state.nativeUsed = true;
      state.armed = false;
      state.eligible = false;
      state.total = 0;
      state.events = 0;
      return;
    }
    if (event.cancelable) event.preventDefault();
    if (this.turnLocked || now < this.wheelIgnoreUntil || this.wheelGestureBlockedUntilQuiet) return;
    if (!state.eligible) { state.armed = true; return; }
    state.total += Math.abs(deltaY);
    state.events++;
    if (state.total < 240 || state.events < 3) return;
    if (this.turn(direction, 'wheel')) {
      this.wheelLastTurnAt = now;
      // Keep a reading exit protected through the whole visible panel transition.
      this.wheelIgnoreUntil = now + (this.reducedMotion.matches ? 500 : 1000);
      this.wheelGestureBlockedUntilQuiet = true;
      this.readingWheel = null;
    }
  }

  canScroll(element, deltaY) {
    if (!element || element.scrollHeight <= element.clientHeight + 1) return false;
    if (deltaY > 0) return element.scrollTop + element.clientHeight < element.scrollHeight - 1;
    if (deltaY < 0) return element.scrollTop > 1;
    return false;
  }

  findScrollable(target, deltaY) {
    if (!(target instanceof Element)) return null;

    let scrollable = target.closest('[data-pageify-scrollable]');
    while (scrollable && scrollable.closest(ROOT_SELECTOR) === this.root) {
      const overflowY = window.getComputedStyle(scrollable).overflowY;
      if ((overflowY === 'auto' || overflowY === 'scroll' || overflowY === 'overlay') && this.canScroll(scrollable, deltaY)) {
        return scrollable;
      }
      scrollable = scrollable.parentElement?.closest('[data-pageify-scrollable]') || null;
    }

    return null;
  }

  onWheel(event) {
    if (event.defaultPrevented || event.ctrlKey || this.isBlockedTarget(event.target)) return;
    const gallery = event.target.closest('slideshow-component[data-monolyt-v22-gallery]');
    if (gallery?.hasAttribute('data-monolyt-wheel-x')) { this.readingWheel = null; return; }
    const readingOwner = this.readingOwner(event.target);
    const { x: deltaX, y: deltaY } = normalizeWheelDeltas(event, gallery?.refs?.scroller || readingOwner || this.root);
    if (Math.abs(deltaY) < 2 || Math.abs(deltaX) >= Math.abs(deltaY)) { this.readingWheel = null; return; }

    const now = window.performance.now();
    const quiet = this.root.querySelector('.product-details') ? 420 : 180;
    const freshGesture = now - this.wheelLastInputAt > quiet;
    if (freshGesture && now >= this.wheelIgnoreUntil) {
      this.wheelDeltaTotal = 0;
      this.wheelGestureBlockedUntilQuiet = false;
      this.wheelGestureUsedNativeScroll = false;
    }
    this.wheelLastInputAt = now;

    if (readingOwner) return this.onReadingWheel(event, readingOwner, deltaY, now);
    this.readingWheel = null;

    const scrollable = this.findScrollable(event.target, deltaY);
    if (scrollable) {
      this.wheelGestureUsedNativeScroll = true;
      return;
    }

    event.preventDefault();
    if (this.wheelGestureUsedNativeScroll && !freshGesture) return;
    if (this.turnLocked || now < this.wheelIgnoreUntil || this.wheelGestureBlockedUntilQuiet) {
      this.wheelDeltaTotal = 0;
      return;
    }
    if (now - this.wheelLastTurnAt < 1000 && !freshGesture && Math.abs(deltaY) < 46) return;

    this.wheelDeltaTotal += deltaY;
    if (Math.abs(this.wheelDeltaTotal) < 58) return;

    const didTurn = this.turn(this.wheelDeltaTotal > 0 ? 1 : -1, 'wheel');
    this.wheelDeltaTotal = 0;
    if (didTurn) {
      this.wheelLastTurnAt = now;
      this.wheelIgnoreUntil = now + 500;
      this.wheelGestureBlockedUntilQuiet = true;
    }
  }

  onTouchStart(event) {
    if (event.touches.length !== 1 || this.isBlockedTarget(event.target)) {
      this.resetTouch();
      return;
    }

    const touch = event.changedTouches[0];
    this.touch = {
      identifier: touch.identifier,
      startX: touch.clientX,
      startY: touch.clientY,
      currentX: touch.clientX,
      currentY: touch.clientY,
      axis: null,
      target: event.target,
      usedNativeScroll: false,
    };
  }

  findTouch(touchList) {
    if (!this.touch) return null;
    return [...touchList].find((touch) => touch.identifier === this.touch.identifier) || null;
  }

  onTouchMove(event) {
    const touchPoint = this.findTouch(event.touches);
    if (!touchPoint || !this.touch) return;
    this.touch.currentX = touchPoint.clientX;
    this.touch.currentY = touchPoint.clientY;
    const deltaX = this.touch.currentX - this.touch.startX;
    const deltaY = this.touch.currentY - this.touch.startY;

    if (!this.touch.axis && Math.max(Math.abs(deltaX), Math.abs(deltaY)) >= 8) {
      this.touch.axis = Math.abs(deltaX) > Math.abs(deltaY) ? 'horizontal' : 'vertical';
    }
    if (this.touch.axis !== 'vertical') return;
    if (this.touch.target.closest('[data-pageify-horizontal]') && Math.abs(deltaX) >= Math.abs(deltaY)) return;

    const scrollable = this.findScrollable(this.touch.target, -deltaY);
    if (scrollable) {
      this.touch.usedNativeScroll = true;
      return;
    }
    event.preventDefault();
  }

  onTouchEnd(event) {
    const touchPoint = this.findTouch(event.changedTouches);
    if (!touchPoint || !this.touch) {
      this.resetTouch();
      return;
    }

    const deltaY = touchPoint.clientY - this.touch.startY;
    const canTurn = this.touch.axis !== 'horizontal'
      && !this.touch.usedNativeScroll
      && Math.abs(deltaY) >= 48
      && !this.turnLocked;
    this.resetTouch();
    if (canTurn) this.turn(deltaY < 0 ? 1 : -1, 'touch');
  }

  resetTouch() {
    this.touch = null;
  }

  hasConstrainedMediaConnection() {
    return hasConstrainedMediaConnection();
  }

  allowsVideoPlayback() {
    return !navigator.connection?.saveData
      && !this.reducedMotion.matches
      && !document.hidden
      && !this.hasConstrainedMediaConnection();
  }

  canAttemptMediaPlayback(media) {
    const background = media.closest('[data-pageify-background]');
    return this.connected
      && media.isConnected
      && background === this.backgrounds[this.index]
      && this.allowsVideoPlayback()
      && media.hasAttribute('data-pageify-autoplay')
      && media.dataset.pageifyMediaState !== 'error';
  }

  canRevealMedia(media) {
    const background = media.closest('[data-pageify-background]');
    return this.connected
      && media.isConnected
      && background === this.backgrounds[this.index]
      && this.allowsVideoPlayback()
      && media.hasAttribute('data-pageify-autoplay')
      && !media.paused
      && media.readyState >= 2
      && media.dataset.pageifyMediaState !== 'error';
  }

  mediaLoopWindow(duration) {
    return Math.min(1, Math.max(0.5, duration * 0.03));
  }

  recordMediaProgress(media) {
    const currentTime = Number.isFinite(media.currentTime) ? media.currentTime : 0;
    const duration = Number.isFinite(media.duration) ? media.duration : 0;
    const previous = this.mediaLoopProgress.get(media);
    let wrappedAt = previous?.wrappedAt ?? Number.NEGATIVE_INFINITY;

    if (media.loop && duration > 0 && previous) {
      const loopWindow = this.mediaLoopWindow(duration);
      if (previous.currentTime >= duration - loopWindow && currentTime <= loopWindow) {
        wrappedAt = window.performance.now();
      }
    }

    this.mediaLoopProgress.set(media, { currentTime, wrappedAt });
  }

  isMediaLoopTransition(media) {
    if (!media.loop || !Number.isFinite(media.duration) || media.duration <= 0) return false;

    const currentTime = Number.isFinite(media.currentTime) ? media.currentTime : 0;
    const loopWindow = this.mediaLoopWindow(media.duration);
    if (media.duration - currentTime <= loopWindow) return true;
    if (currentTime > loopWindow) return false;

    const progress = this.mediaLoopProgress.get(media);
    if (progress?.currentTime >= media.duration - loopWindow) return true;
    return window.performance.now() - (progress?.wrappedAt ?? Number.NEGATIVE_INFINITY) <= 1_000;
  }

  deferLoopBufferConfirmation(media) {
    if (this.mediaBufferChecks.has(media)) return;
    const startingTime = Number.isFinite(media.currentTime) ? media.currentTime : 0;
    const timer = this.schedule(() => {
      this.mediaBufferChecks.delete(media);
      if (media.dataset.pageifyMediaState !== 'ready') return;
      if (!this.canAttemptMediaPlayback(media)) {
        this.setMediaState(media, 'poster');
        return;
      }

      const currentTime = Number.isFinite(media.currentTime) ? media.currentTime : 0;
      const playbackAdvanced = Math.abs(currentTime - startingTime) >= 0.05;
      if (this.canRevealMedia(media) && (media.readyState >= 3 || playbackAdvanced)) {
        this.setMediaState(media, 'ready');
      } else {
        this.setMediaState(media, 'loading', true);
      }
    }, 400);
    this.mediaBufferChecks.set(media, timer);
  }

  bindMediaState(media) {
    if (!(media instanceof HTMLMediaElement) || media.dataset.pageifyMediaBound === 'true') return;
    media.dataset.pageifyMediaBound = 'true';
    const mediaController = new AbortController();
    this.mediaListenerControllers.set(media, mediaController);
    const signal = mediaController.signal;

    media.addEventListener('canplay', () => {
      if (this.canAttemptMediaPlayback(media)) this.setMediaState(media, 'loading', true);
      else this.setMediaState(media, 'poster');
    }, { signal });
    media.addEventListener('playing', () => {
      if (this.canRevealMedia(media)) {
        this.recordMediaProgress(media);
        this.setMediaState(media, 'ready');
      } else {
        media.pause();
        this.setMediaState(media, 'poster');
      }
    }, { signal });
    media.addEventListener('timeupdate', () => this.recordMediaProgress(media), { signal });
    media.addEventListener('waiting', () => this.handleMediaBuffering(media), { signal });
    media.addEventListener('stalled', () => this.handleMediaBuffering(media), { signal });
    media.addEventListener('pause', () => {
      if (media.dataset.pageifyMediaState !== 'error') this.setMediaState(media, 'poster');
    }, { signal });
    media.addEventListener('error', () => {
      this.setMediaState(media, 'error');
    }, { signal });
  }

  setMediaState(media, state, showLoader = false) {
    media.dataset.pageifyMediaState = state;
    const loader = media.parentElement?.querySelector('[data-pageify-media-loader]');
    if (!(loader instanceof HTMLElement)) return;

    if (state === 'ready' || state === 'error') loader.hidden = true;
    else loader.hidden = !showLoader;
  }

  handleMediaBuffering(media) {
    if (media.dataset.pageifyMediaState === 'ready' && this.isMediaLoopTransition(media)) {
      this.setMediaState(media, 'ready');
      this.deferLoopBufferConfirmation(media);
    } else if (
      media.dataset.pageifyMediaState === 'ready'
      && this.canRevealMedia(media)
      && media.readyState >= 3
    ) {
      this.setMediaState(media, 'ready');
    } else if (this.canAttemptMediaPlayback(media)) {
      this.setMediaState(media, 'loading', true);
    } else if (media.dataset.pageifyMediaState !== 'error') {
      this.setMediaState(media, 'poster');
    }
  }

  hydrateMedia(media) {
    if (media.dataset.pageifyHydrated === 'true') return;
    const lazyElements = [media, ...media.querySelectorAll('[data-src], [data-srcset]')];
    lazyElements.forEach((element) => {
      if (element.dataset.src) element.src = element.dataset.src;
      if (element.dataset.srcset) element.srcset = element.dataset.srcset;
    });
    media.dataset.pageifyHydrated = 'true';
    if (media instanceof HTMLMediaElement) media.load();
  }

  syncMedia() {
    const allowVideoPlayback = this.allowsVideoPlayback();
    this.backgrounds.forEach((background, backgroundIndex) => {
      const active = backgroundIndex === this.index;
      for (const media of background.querySelectorAll('[data-pageify-media]')) {
        if (!(media instanceof HTMLMediaElement)) continue;
        this.bindMediaState(media);
        const playAttempt = (this.mediaPlayAttempts.get(media) ?? 0) + 1;
        this.mediaPlayAttempts.set(media, playAttempt);

        if (media.dataset.pageifyMediaState === 'error') {
          media.pause();
          continue;
        }

        if (active && allowVideoPlayback && media.hasAttribute('data-pageify-autoplay')) {
          this.hydrateMedia(media);
          if (media.dataset.pageifyMediaState === 'ready' && this.canRevealMedia(media)) {
            this.setMediaState(media, 'ready');
          } else {
            this.setMediaState(media, 'loading', true);
            const playPromise = media.play();
            playPromise
              ?.then?.(() => {
                if (this.mediaPlayAttempts.get(media) === playAttempt && this.canRevealMedia(media)) {
                  this.setMediaState(media, 'ready');
                }
              })
              ?.catch?.(() => {
                if (
                  this.mediaPlayAttempts.get(media) !== playAttempt ||
                  !this.canAttemptMediaPlayback(media) ||
                  !media.paused
                ) return;

                this.setMediaState(media, 'poster');
              });
          }
        } else {
          media.pause();
          this.setMediaState(media, 'poster');
        }
      }
    });
  }

  pauseMedia() {
    this.backgrounds.forEach((background) => {
      background.querySelectorAll('[data-pageify-media]').forEach((media) => {
        if (media instanceof HTMLMediaElement) {
          media.pause();
          this.setMediaState(media, 'poster');
        }
      });
    });
  }

  releaseMediaRegistry(backgrounds) {
    backgrounds.forEach((background) => {
      background.querySelectorAll('[data-pageify-media]').forEach((media) => {
        if (!(media instanceof HTMLMediaElement)) return;

        const bufferTimer = this.mediaBufferChecks.get(media);
        if (bufferTimer !== undefined) {
          window.clearTimeout(bufferTimer);
          this.timers.delete(bufferTimer);
          this.mediaBufferChecks.delete(media);
        }
        this.mediaPlayAttempts.set(media, (this.mediaPlayAttempts.get(media) ?? 0) + 1);
        this.mediaLoopProgress.delete(media);
        this.mediaListenerControllers.get(media)?.abort();
        this.mediaListenerControllers.delete(media);
        delete media.dataset.pageifyMediaBound;
        if (media.dataset.pageifyMediaState === 'error') delete media.dataset.pageifyHydrated;
        media.pause();
        this.setMediaState(media, 'poster');
      });
    });
  }

  rebuild() {
    const activeKey = this.panelKeys[this.index];
    const previousBackgrounds = [...this.backgrounds];
    if (!this.portalBackgroundLayers() || !this.buildRegistry()) {
      this.destroy();
      return;
    }
    this.releaseMediaRegistry(previousBackgrounds.filter((background) => !this.backgrounds.includes(background)));
    const preservedIndex = this.panelKeys.indexOf(activeKey);
    this.commitState(preservedIndex >= 0 ? preservedIndex : Math.min(this.index, this.panels.length - 1), 0);
    this.setupMobileProductSheet();
  }

  destroy() {
    if (!this.connected) return;
    this.connected = false;
    this.sequence += 1;
    this.abortController.abort();
    this.mobileProductSheetCleanup?.();
    this.backgroundResizeObserver?.disconnect();
    this.reducedMotion.removeEventListener?.('change', this.onMotionPreferenceChange);
    this.connection?.removeEventListener?.('change', this.onConnectionChange);
    this.timers.forEach((timer) => window.clearTimeout(timer));
    this.timers.clear();

    this.panels.forEach((panel, index) => {
      panel.classList.toggle('is-active', index === 0);
      panel.classList.remove('is-leaving');
      panel.removeAttribute('aria-hidden');
      panel.removeAttribute('inert');
      panel.style.removeProperty('--pageify-offset');
      panel.style.removeProperty('z-index');
    });
    this.releaseMediaRegistry(this.backgrounds);
    this.backgrounds.forEach((background, index) => {
      background.classList.toggle('is-active', index === 0);
      background.removeAttribute('aria-hidden');
      background.style.removeProperty('--pageify-offset');
    });
    this.restoreBackgroundLayers();
    this.root.classList.remove('is-enhanced');
    this.root.style.removeProperty('--pageify-header-gap');
    delete this.root.dataset.pageifyActive;
    delete this.root.dataset.pageifyDirection;
    delete this.root.dataset.turnDirection;
    this.navigation.setAttribute('data-pageify-unready', '');
    this.navigation.setAttribute('aria-hidden', 'true');
    this.previousButton.setAttribute('tabindex', '-1');
    this.nextButton.setAttribute('tabindex', '-1');
    this.previousButton.disabled = true;
    this.nextButton.disabled = true;
    this.navigation.removeAttribute('data-pageify-at-end');
    if (!this.navigationHadAriaControls) this.navigation.removeAttribute('aria-controls');
    instances.delete(this.root);
    liveInstances.delete(this);
    if (!liveInstances.size) document.body.classList.remove('pageify-is-enhanced');
  }
}

function initializeRoot(root) {
  const existing = instances.get(root);
  if (existing) {
    existing.rebuild();
    return existing;
  }

  const instance = new MonolytPageify(root);
  if (!instance.connected) return null;
  instances.set(root, instance);
  liveInstances.add(instance);
  return instance;
}

function rootsWithin(scope) {
  const roots = [];
  if (scope instanceof Element && scope.matches(ROOT_SELECTOR)) roots.push(scope);
  scope.querySelectorAll?.(ROOT_SELECTOR).forEach((root) => roots.push(root));
  return roots;
}

function initializeWithin(scope = document) {
  rootsWithin(scope).forEach((root) => initializeRoot(root));
}

function initializeAffectedRoot(scope) {
  initializeWithin(scope);
  const containingRoot = scope instanceof Element ? scope.closest(ROOT_SELECTOR) : null;
  if (containingRoot && containingRoot !== scope) initializeRoot(containingRoot);
}

initializeWithin();
if (!liveInstances.size) document.documentElement.classList.remove('monolyt-pageify-pending');

document.addEventListener('shopify:section:load', (event) => initializeAffectedRoot(event.target));
document.addEventListener('shopify:section:reorder', (event) => initializeAffectedRoot(event.target));
document.addEventListener('shopify:section:unload', (event) => {
  const containingRoot = event.target.closest?.(ROOT_SELECTOR);
  instances.get(containingRoot)?.restoreBackgroundLayers(event.target);
  rootsWithin(event.target).forEach((root) => instances.get(root)?.destroy());
  if (containingRoot && containingRoot !== event.target) {
    queueMicrotask(() => initializeRoot(containingRoot));
  }
});
document.addEventListener('shopify:block:select', (event) => {
  const panel = event.target.closest?.('[data-pageify-panel]');
  const root = panel?.closest(ROOT_SELECTOR);
  const instance = root ? instances.get(root) : null;
  const index = instance?.panels.indexOf(panel) ?? -1;
  if (index >= 0) instance.goTo(index, 'shopify-editor');
});
document.addEventListener('shopify:section:select', (event) => {
  const panel = event.target.closest?.('[data-pageify-panel]')
    || event.target.querySelector?.('[data-pageify-panel]');
  const root = panel?.closest(ROOT_SELECTOR);
  const instance = root ? instances.get(root) : null;
  const index = instance?.panels.indexOf(panel) ?? -1;
  if (index >= 0) instance.goTo(index, 'shopify-editor');
});
